import { apiRequest } from '@/lib/api/client'
import type {
  FetchInstitutionDonorsOptions,
  FetchInstitutionDonorsResponse,
  InstitutionDonorDetail,
  InstitutionDonorNumbersResponse,
  InstitutionDonorOrder,
  InstitutionDonorOrdersResponse,
  InstitutionDonorOrderNumbersResponse,
  InstitutionDonorUpdatePayload,
  InstitutionDonorAchievementsResponse,
} from '@/types/institution-donor'

export function fetchInstitutionDonorAchievements(
  donorId: number,
  page = 1,
  token?: string | null,
  signal?: AbortSignal
) {
  return apiRequest<InstitutionDonorAchievementsResponse>(
    `/api/institution/donors/${donorId}/achievements?page=${page}`,
    {
      requireAuth: true,
      token: token ?? undefined,
      signal,
      cache: 'no-store',
    }
  )
}

export function fetchInstitutionDonorOrders(
  donorId: number,
  page = 1,
  token?: string | null
) {
  return apiRequest<InstitutionDonorOrdersResponse>(
    `/api/institution/donors/${donorId}/orders?page=${page}`,
    { requireAuth: true, token: token ?? undefined }
  )
}

export function fetchInstitutionDonorOrderNumbers(
  donorId: number,
  paymentId: number,
  page = 1,
  token?: string | null
) {
  return apiRequest<InstitutionDonorOrderNumbersResponse>(
    `/api/institution/donors/${donorId}/orders/${paymentId}/numbers?page=${page}`,
    { requireAuth: true, token: token ?? undefined }
  )
}

export function cancelInstitutionDonorOrder(
  donorId: number,
  paymentId: number,
  reason: string,
  code: string,
  token?: string | null
) {
  return apiRequest<InstitutionDonorOrder>(
    `/api/institution/donors/${donorId}/orders/${paymentId}/cancel`,
    {
      method: 'POST',
      body: JSON.stringify({ reason, code }),
      requireAuth: true,
      token: token ?? undefined,
    }
  )
}

export async function fetchInstitutionDonors(
  q: string,
  options: FetchInstitutionDonorsOptions = {}
): Promise<FetchInstitutionDonorsResponse> {
  const params = new URLSearchParams()
  params.set('q', q)
  if (options.campaign_id !== undefined) {
    params.set('campaign_id', String(options.campaign_id))
  }
  const page = Math.max(1, options.page ?? 1)
  const perPage = Math.min(50, Math.max(1, options.per_page ?? 20))
  params.set('page', String(page))
  params.set('per_page', String(perPage))
  if (options.purchase_status && options.purchase_status !== 'all') {
    params.set('purchase_status', options.purchase_status)
  }
  if (options.min_spent !== undefined) params.set('min_spent', String(options.min_spent))
  if (options.max_spent !== undefined) params.set('max_spent', String(options.max_spent))
  if (options.sort && options.sort !== 'recent') params.set('sort', options.sort)

  return apiRequest<FetchInstitutionDonorsResponse>(
    `/api/institution/donors?${params.toString()}`,
    { method: 'GET', requireAuth: true }
  )
}

export async function fetchInstitutionDonorNumbers(
  donorId: number,
  page = 1,
  token?: string | null,
  rouletteOnly = false
): Promise<InstitutionDonorNumbersResponse> {
  return apiRequest<InstitutionDonorNumbersResponse>(
    `/api/institution/donors/${donorId}/numbers?page=${Math.max(1, page)}&per_page=48${rouletteOnly ? '&origin=roulette' : ''}`,
    { method: 'GET', requireAuth: true, token: token ?? undefined }
  )
}

export async function fetchInstitutionDonorDetail(
  donorId: number,
  token?: string | null
): Promise<InstitutionDonorDetail> {
  const detail = await apiRequest<InstitutionDonorDetail>(
    `/api/institution/donors/${donorId}`,
    {
      method: 'GET',
      requireAuth: true,
      token: token ?? undefined,
    }
  )

  return {
    ...detail,
    questionnaire: {
      answers: detail.questionnaire?.answers ?? [],
    },
  }
}

export async function updateInstitutionDonor(
  donorId: number,
  payload: InstitutionDonorUpdatePayload,
  token?: string | null
): Promise<InstitutionDonorDetail> {
  const body: Record<string, unknown> = {
    first_name: payload.first_name,
    last_name: payload.last_name,
    ...(payload.corporate_name !== undefined
      ? { corporate_name: payload.corporate_name }
      : {}),
    email: payload.email,
    phone: payload.phone,
    document: payload.document,
    website: payload.website,
    address: payload.address,
  }
  if (payload.password != null && payload.password !== '') {
    body.password = payload.password
  }
  const detail = await apiRequest<InstitutionDonorDetail>(
    `/api/institution/donors/${donorId}`,
    {
      method: 'PUT',
      body: JSON.stringify(body),
      requireAuth: true,
      token: token ?? undefined,
    }
  )

  return {
    ...detail,
    questionnaire: {
      answers: detail.questionnaire?.answers ?? [],
    },
  }
}

export async function sendInstitutionDonorPasswordReset(
  donorId: number
): Promise<string> {
  const response = await apiRequest<{ message: string }>(
    `/api/institution/donors/${donorId}/password-reset`,
    { method: 'POST', requireAuth: true }
  )
  return (
    response.message ??
    'Link de recuperação enviado para os contatos cadastrados na Sortee.'
  )
}

/** Valor ausente para destacar em vermelho na UI. */
export function isFieldMissing(value: string | null | undefined): boolean {
  if (value === null || value === undefined) return true
  return String(value).trim() === ''
}
